<template>
    <div class="relative">
        <vx-spinner v-if="retrieving" :scale="0.5" class="retrieval-spinner" />
        <vx-auto-suggest-modified
            v-else
            :value="searchText"
            ref="autoSuggest"
            v-bind="attrs"
            background-overlay
            class="w-full vx-elastic-suggest"
            input-classes="w-full vs-input-no-shdow-focus"
            :disabled="disabled || retrieving"
            :icon="icon"
            :autofocus="autofocus || showFullSearch"
            :data="groups"
            :clearable="clearable && !retrieving"
            :select-like="selectLike"
            :selected.sync="selected"
            :show-on-focus="showOnFocus"
            @input="searchChanged"
            @blur="(e) => $emit('blur', e)"
            @focus="(e) => $emit('focus', e)"
            @hovered="(e) => $emit('hovered', e)"
            @closeSearchbar="showFullSearch = false"
            @load-more="fetchRecords"
        >
            <template v-slot:group="{ group_name }">
                <slot name="group" :group_name="group_name">
                    <p class="font-semibold text-primary">
                        {{ group_name }}
                    </p>
                </slot>
            </template>

            <!-- Pages Suggestion -->
            <template
                v-for="(group, group_name, index) in groups"
                v-slot:[group_name]="{ suggestion }"
            >
                <div class="select-item" :key="getId(group, suggestion, index)">
                    <slot :name="group_name" :suggestion="suggestion">
                        <div class="flex items-center leading-none py-1">
                            <vx-basic-icon
                                v-if="suggestion.useMdi"
                                :use-mdi="suggestion.useMdi"
                                :icon="suggestion.icon"
                                :outlined="suggestion.outlined"
                                class="mr-4 self-start"
                            />
                            <feather-icon
                                v-else
                                :icon="getListIcon(suggestion)"
                                svgClasses="h-6 w-6 "
                                class="mr-4"
                            />
                            <span>{{ group.getDisplayText(suggestion) }}</span>
                        </div>
                    </slot>
                </div>
            </template>

            <!-- Loading Slot -->
            <template v-slot:loader>
                <vx-spinner :scale="0.6" :type="loaderState == 'listening' ? 'sound' : 'default'" />
            </template>

            <!-- No Items Slot -->
            <template v-slot:noResult="{ group_name }">
                <slot name="noResult" :group_name="group_name">
                    <div class="flex items-center py-3 px-4">
                        <feather-icon icon="InfoIcon" svgClasses="h-6 w-6" class="mr-4" />
                        <span>No {{ group_name }} found.</span>
                    </div>
                </slot>
            </template>
        </vx-auto-suggest-modified>
    </div>
</template>

<script>
import VxAutoSuggestModified from './VxAutoSuggestModified'

export default {
    name: 'elastic-suggest',
    components: {
        VxAutoSuggestModified
    },
    props: {
        search_groups: {
            type: Object,
            required: true,
            validator: (groups) => {
                // must have an Array data prop OR a String searchUrl
                // Must have a idfield and searchOn prop to designate internal properies of the data objects
                // searchOn can be a function or a string allowing concatenation of multiple fields
                return Object.values(groups).every((group_data) => {
                    if ('searchUrl' in group_data) {
                        if (typeof group_data['searchUrl'] !== 'string') {
                            return false
                        } else if (!('idfield' in group_data)) {
                            return false
                        }
                        if ('getUrl' in group_data && typeof group_data['getUrl'] !== 'string') {
                            return false
                        }
                    } else if ('data' in group_data) {
                        if (!Array.isArray(group_data['data'])) {
                            return false
                        }
                    } else {
                        return false
                    }

                    if (
                        'idfield' in group_data &&
                        typeof group_data['idfield'] !== 'string' &&
                        typeof group_data['idfield'] !== 'function'
                    ) {
                        return false
                    }

                    if (
                        'searchOn' in group_data &&
                        typeof group_data['searchOn'] !== 'string' &&
                        typeof group_data['searchOn'] !== 'function'
                    ) {
                        return false
                    }
                    return true
                })
            }
        },
        icon: {
            type: String,
            default: 'SearchIcon'
        },
        listIcon: {
            type: [String, Function],
            default: 'ChevronRightIcon'
        },
        value: {
            type: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        batchSize: {
            type: Number,
            default: 20
        },
        listenFor: {
            type: Number,
            default: 500
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        selectLike: {
            type: Boolean,
            default: true
        },
        showOnFocus: {
            type: Boolean,
            default: false
        },
        displayKey: {
            type: [String, Function]
        },
        clearable: {
            type: Boolean,
            default: true
        }
    },
    watch: {
        search_groups: {
            handler: function (groups) {
                if (groups == null) {
                    return
                }
                var current_groups = Object.keys(this.groups)
                var new_groups = Object.keys(groups)
                current_groups.forEach((current_group_name) => {
                    if (!new_groups.includes(current_group_name)) {
                        delete this.groups[current_group_name]
                    }
                })
                new_groups.forEach((group_name) => {
                    var group = groups[group_name]
                    if (!(group_name in this.groups)) {
                        const group_obj = {
                            searchUrl: null,
                            getUrl: null,
                            getSearchText: null,
                            getDisplayText: null,
                            id: null,
                            data: null,
                            loading: false,
                            cachedSearches: {},
                            cachedRetrievals: {},
                            isPreSorted: false,
                            filters: {},
                            customSearchBy: null
                        }

                        this.$set(this.groups, group_name, group_obj)
                    }

                    var grp = this.groups[group_name]
                    grp.name = group_name
                    grp.searchUrl = group.searchUrl
                    grp.getUrl = group.getUrl

                    if (typeof group.searchOn === 'string') {
                        grp.getSearchText = (suggestion) => suggestion[group.searchOn]
                    } else {
                        grp.getSearchText = group.searchOn
                    }

                    if (!group.displayText) {
                        grp.getDisplayText = grp.getSearchText
                    } else if (typeof group.displayText === 'string') {
                        grp.getDisplayText = (suggestion) => suggestion[group.displayText]
                    } else {
                        grp.getDisplayText = group.displayText
                    }
                    grp.customSearchBy = group.customSearchBy || 'search'
                    grp.useIdInPath = !!group.useIdInPath
                    grp.id = group.idfield
                    grp.data = group.data || []
                    grp.filters = group.filters || {}
                    // if using server side filtering, tell the autosuggest not to filter again
                    // ie. Not using local data, and providing an external search endpoint
                    grp.isPreSorted = (!grp.data || !grp.data.any()) && !!grp.searchUrl
                    grp.cachedSearches = {}
                })
                this.preCache()
            },
            immediate: true,
            deep: true
        },
        batchSize() {
            Object.values(this.groups).forEach((group) => {
                group.cachedSearches = {}
            })
        },
        searchText: {
            handler: function (val) {
                this.$emit('search-changed', val)
            }
        }
    },
    data() {
        return {
            retrieving: false,
            searchText: '',
            showFullSearch: false,
            typingTimeout: null,
            groups: {},
            currentFetch: Promise.resolve()
        }
    },
    computed: {
        getDisplayText() {
            if (typeof this.displayKey === 'function') {
                return this.displayKey
            } else if (this.displayKey) {
                return (v) => v[this.displayKey]
            }
            return (v) => (v ? this.searchText : '')
        },
        getListIcon() {
            if (typeof this.listIcon === 'function') {
                return this.listIcon
            }
            return () => this.listIcon
        },
        attrs() {
            return { ...this.$attrs, selected: null }
        },
        selected: {
            get() {
                return this.value
            },
            set(val) {
                this.onSelected(val)
            }
        },
        loaderState() {
            if (this.typingTimeout != null) {
                return 'listening'
            } else {
                return 'loading'
            }
        },
        groupsLoading: {
            get() {
                return Object.values(this.groups).any((g) => g.loading)
            },
            set(v) {
                Object.values(this.groups).forEach((group) => {
                    group.loading = v
                })
            }
        },
        isListeningForUserInput() {
            return !!this.typingTimeout
        }
    },
    methods: {
        getId(group, item, index) {
            if (!group.id) {
                return index
            }
            if (typeof group.id === 'string') {
                return item[group.id]
            }
            if (typeof group.id !== 'function') {
                return index
            }

            var key = group.id(item)
            var keyType = typeof key
            if (keyType === 'object' || keyType == 'function') {
                return index
            }
            return key
        },
        async onSelected({ item, groupName }) {
            var group = this.groups[groupName]
            try {
                if (groupName) {
                    var result = item
                    var id = item[group.id]

                    if (group.getUrl && id != null) {
                        // dont use falsey (!id) as 0 could be a valid id
                        result = group.cachedRetrievals[id]
                        if (!result) {
                            this.retrieving = true
                            this.$emit('retrieving', item)
                            var url = ''
                            if (group.useIdInPath) {
                                url = `${group.getUrl}/${id}`
                            } else {
                                url = `${group.getUrl}?id=${id}`
                            }
                            var response = await this.$api.get(url)
                            result = response.data

                            group.cachedRetrievals[id] = result
                        }
                    }
                    this.$emit('input', result)
                    this.$emit('selected', result)
                } else {
                    this.$emit('error')
                }
            } catch (err) {
                console.error(err)
                this.$emit('error', item)
                this.notifyServerError(err)
            } finally {
                this.retrieving = false
                this.$nextTick(async function () {
                    await this.validate()
                })
            }
            this.showFullSearch = false
        },
        searchChanged(term) {
            this.searchText = term
            if (this.isListeningForUserInput) {
                clearTimeout(this.typingTimeout)
                this.typingTimeout = null
                this.groupsLoading = false
            }

            if (term) {
                this.groupsLoading = true
                this.typingTimeout = setTimeout(
                    function () {
                        this.typingTimeout = null
                        this.groupsLoading = false
                        Object.values(this.groups).forEach((group) => {
                            this.fetchRecords(group)
                        })
                    }.bind(this),
                    this.listenFor
                )
            } else if (!term && this.showOnFocus) {
                this.preCache()
            } else if (this.selectLike) {
                // if we are setting the value back to null from non-null
                // without this check, it fires on load for every elastic
                if (this.value) {
                    this.$emit('input', null)
                    this.preCache()
                }
            }
        },
        async fetchRecords(group) {
            if (typeof group === 'string') {
                group = this.groups[group]
            }
            if (!group.searchUrl) {
                // todo: could perform chunking of local data here to prevent high render times on large data sets
                return
            }

            var term = this.searchText
            var requestedPage = 0
            await this.currentFetch
            var cache = this.tryGetCache(group, term)

            if (cache) {
                if (cache.complete) {
                    // search is already cached up to the required page
                    group.data = cache.data
                    return
                }
                requestedPage = cache.page + 1
            }

            try {
                group.loading = true
                var params = {
                    ...group.filters,
                    pageSize: this.batchSize,
                    pageNo: requestedPage,
                    [group.customSearchBy]: term
                }

                var result = await this.$api.get(group.searchUrl, { params })
                if (result && result.data) {
                    this.cacheSearch(group, term, result.data, requestedPage)
                    cache = this.tryGetCache(group, term)
                    group.data = cache.data
                }
            } catch (err) {
                console.error(err)
                this.notifyServerError(err)
            }
            group.loading = false
        },
        cacheSearch(group, term, results, page) {
            // DC NOTE: Bug/difference with c#, c# result returns {data: [stuff]}
            // php does result = [stuff]
            if (results.data) {
                results = results.data
            }

            var cacheKey = this.getCacheKey(group, term)
            var currentCache = this.getCacheByKey(group, cacheKey)
            var currentResultSet = (currentCache && currentCache.data) || []

            currentCache = {
                page: page,
                // concat results, maintain server sort order
                data: currentResultSet.concat(results),
                // no more search results available ?
                complete: results.length < this.batchSize
            }

            group.cachedSearches[cacheKey] = currentCache
            return currentCache
        },
        tryGetCache(group, term) {
            var cacheKey = this.getCacheKey(group, term)
            if (!cacheKey) {
                return this.getCacheByKey(group, term)
            }

            if (!this.getCacheByKey(group, cacheKey)) {
                var isBlocked = Object.keys(group.cachedSearches).any((key) => {
                    var cache = this.getCacheByKey(group, key)

                    var [existingTerm, existingHash] = key.splitOnLastOccurrence('_')
                    var [term, hash] = cacheKey.splitOnLastOccurrence('_')

                    return (
                        existingHash == hash &&
                        term.contains(existingTerm) &&
                        cache.page === 0 &&
                        !cache.data.any()
                    )
                })

                if (isBlocked) {
                    this.cacheSearch(group, term, [], 0)
                }
            }
            return this.getCacheByKey(group, cacheKey)
        },
        getCacheByKey(group, cacheKey) {
            return group.cachedSearches[cacheKey]
        },
        getCacheByTerm(group, searchTerm) {
            var cacheKey = this.getCacheKey(group, searchTerm)
            return this.getCacheByKey(group, cacheKey)
        },
        getCacheKey(group, searchTerm) {
            var filterHash = ''
            if (group.filters && Object.keys(group.filters).any()) {
                filterHash = `_${JSON.stringify(group.filters || {}).hash()}`
            }
            return `${searchTerm && searchTerm.toLowerCase()}${filterHash}`
        },
        focus() {
            this.$refs.autoSuggest.focus()
        },
        async preCache() {
            if (this.showOnFocus) {
                await Promise.all(
                    Object.values(this.groups).map((group) => {
                        return this.fetchRecords(group)
                    })
                )
            }
        },
        close() {
            this.$refs.autoSuggest.close()
        }
    },
    async mounted() {
        // await this.preCache()
    }
}
</script>

<style lang="scss" scoped>
.retrieval-spinner {
    z-index: 70000;
    position: absolute;
    right: 1.5rem;
    top: 0;
    bottom: 0;
    margin: auto;
}

.select-item {
    .feather-icon {
        transition: all 0.1s ease;
    }
    :hover > & {
        .feather-icon {
            margin-right: 1.5rem !important;
        }
    }
}
</style>
